import type { LocaleBundle } from "../types";

export const bundleEn: LocaleBundle = {
  locale: "en",
  config: {
    siteUrl: "https://ebuza.dev",
    name: "Enrico",
    surname: "Buza",
    nickname: "Moldaviano",
    role: "Web Developer",
    specialty: "Designer",
    about:
      "Product-minded full stack developer: solid backend, polished frontend, with a focus on security and performance. Projects for SMBs, e-commerce, and bespoke applications.",
    aboutHeadline: "Code that supports the business and interfaces that do not get in the way",
    aboutSubhead:
      "I am Enrico Buza, a web developer with a strong backend background and an eye for UX and craft. I help teams and clients go from an idea to a reliable, measurable web product that is ready to scale.",
    aboutStory: `I build web applications where data, integrations, and clarity for daily users matter: from brochure sites and landing pages to e-commerce and platforms with dashboards and payments.

My path runs through Alloy S.r.l., where I work on the backend (Node.js, TypeScript, microservices, MySQL, Neo4j) with attention to privacy, security, and performance. In parallel I grow the front end with React and modern tooling—because a solid API deserves an interface that communicates well.

I have hands-on experience with real clients, local businesses, retail, and bespoke projects. I turn vague requests into concrete requirements, realistic timelines, and solutions you can maintain over time.`,
    aboutValueProps: [
      {
        title: "Serious stack, tangible outcomes",
        description:
          "TypeScript, React, Node.js, SQL, and graphs when they are needed. I prefer solutions that are easy to evolve over fragile demos that only impress at first glance.",
      },
      {
        title: "From the first call to go-live",
        description:
          "I support you with light discovery, prioritisation, estimates, and iterations—whether you are a client with a defined budget or a team onboarding a technical role.",
      },
      {
        title: "Clear communication",
        description:
          "Updates without needless jargon, documentation where it matters, and room for questions—so recruiters, PMs, and stakeholders always know where we stand.",
      },
    ],
    aboutAvailability:
      "Open to opportunities in structured teams and to collaborations on freelance projects / sites and digital products. Write me with a role or project idea: I will reply with availability and next steps.",
    aboutQuickFacts: [
      { label: "Current role", value: "Web Developer — Backend @ Alloy S.r.l." },
      { label: "Education", value: "Diploma IIS Levi Ponti, Mirano (VE) — 80/100" },
      { label: "Base", value: "Camposampiero (PD) · Italy" },
    ],
    social: {
      linkedin: "https://www.linkedin.com/in/enrico-buza-9b6992257/",
      github: "https://github.com/Moldaviano",
    },
    contactHeadline: "Let us build something solid, together",
    contactSubhead:
      "If you are a recruiter with an opening, a founder with a site or product in mind, or a team looking for technical help: get in touch. I prefer concrete conversations about goals, timelines, and ways of working—no endless back-and-forth.",
    contactHighlights: [
      {
        title: "For hiring teams",
        description:
          "Portfolio, GitHub, and in-house experience on backends and microservices. I can go deeper on stack, ways of working, and availability in a short email or call.",
      },
      {
        title: "For project owners",
        description:
          "From brochure sites to more structured platforms: I help you understand what is realistic in phase one, what can wait, and how to iterate without surprises.",
      },
      {
        title: "What to expect",
        description:
          "A structured reply—no ghosting: if the project is not a fit I will say so; otherwise I will propose clear next steps.",
      },
    ],
    contactFormHint:
      "In a few lines, describe what you need (e.g. “brochure site quote”, “full stack application”, “API collaboration”) so I can understand the context immediately.",
    contactResponsePledge: "I usually reply within 1–2 business days.",
    phone: "+39 389 650 8459",
    locationDisplay: "Camposampiero (PD), Italy",
    projects: [
      {
        projectId: "1",
        title: "Pizzeria La Gustosa",
        description:
          "Brochure website for Pizzeria La Gustosa; I am implementing an online ordering flow with secure Stripe payments and management of menu items, products, prices, ingredients, and more.",
        url: "https://lagustosapizzeria.com/",
        clientName: "Pizzeria La Gustosa",
        technologies: ["React", "CSS", "Responsive Design"],
        references: "150% increase in online views in the first 3 months",
      },
      {
        projectId: "1",
        title: "Luxury Lounge Bar",
        description: "Brochure site for a lounge bar with multiple sections and pages.",
        url: "https://pub-showcase-0001.ebuza.dev//",
        imgUrl:
          "https://iad.microlink.io/qAjCxkS_jXkS9albFp2NMw2afZR6XKvQKMXqUcFBQ4yNIFvSqv6YEYkZBWVFQCZPaH5AdgJIMvtyn1LSVqntoA.png",
        clientName: "Pub Downtown",
        technologies: ["React", "TailwindCSS", "Framer Motion", "Vercel"],
        references: "Owner happy with how the venue is presented online",
      },
    ],
    experiences: [
      {
        id: "1",
        role: "Training internship — Web Developer",
        company: "Alloy S.r.l.",
        description:
          "During this internship I worked across frontend and backend. I used AngularJS to build a prototype (POC) to assess how UI elements affect page performance. In parallel I deepened the backend with Node.js (TypeScript) on a proprietary framework, contributing to features within a microservices architecture.",
        startDate: "June 2022",
        endDate: "June 2022",
        location: "Mirano (VE), Italy",
        icon: "💻",
        achievements: [
          "Hands-on with Node.js",
          "Hands-on with AngularJS",
          "Worked on a microservices architecture",
          "POC implementation to measure page performance",
        ],
      },
      {
        id: "2",
        role: "Erasmus training internship — WordPress/Shopify Developer",
        company: "El Garbo de Greta",
        description:
          "I optimised two websites. On WordPress I made structural changes to a digital marketing and Airbnb services brochure site. On Shopify I improved a fashion e-commerce store with new search filters and catalogue updates, improving usability.",
        startDate: "July 2022",
        endDate: "August 2022",
        location: "Seville (ES), Spain",
        icon: "🚀",
        achievements: [
          "Hands-on with WordPress",
          "Hands-on with Shopify",
          "Implemented e-commerce filters",
        ],
      },
      {
        id: "3",
        role: "Web Developer — Backend",
        company: "Alloy S.r.l.",
        description:
          "Backend developer using Node.js (TypeScript), MySQL, and Neo4j. I work on several proprietary frameworks, all microservices-based, applying backend best practices with strong attention to privacy, security, and performance.",
        startDate: "August 2023",
        endDate: "Present",
        location: "Mirano (VE), Italy",
        icon: "🎓",
        achievements: [
          "Completed internal React training",
          "Contributed to 8 projects",
          "JavaScript Level 1 certification",
        ],
      },
    ],
    email: "enrico.buza@ebuza.dev",
    skills: ["Typescript", "Javascript", "HTML5", "CSS", "TailwindCSS", "React", "SQL", "Neo4J", "MongoDB"],
    professionalServiceName: "Websites, APIs, and digital products",
    professionalServiceDescription:
      "Full stack web development for teams and SMBs: brochure sites, e-commerce, Node.js backends, and React/TypeScript frontends.",
    countryServed: "Italy",
    metaSiteBrandSuffix: "Professional portfolio",
  },
  certificates: [
    {
      title: "Introduction to Packet Tracer",
      imgUrl: "/ciscoIntroductionToPacketTracerCertificate.png",
      description: "Completion certificate for Cisco Networking Academy’s Introduction to Packet Tracer course.",
      category: "Networking",
      organization: "Cisco",
      date: "2022",
      credentialId: "",
    },
    {
      title: "Introduction to Cybersecurity",
      imgUrl: "/ciscoIntroductionToCybersecurityCertificate.png",
      description:
        "Completion certificate for Introduction to Cybersecurity from Cisco Networking Academy, covering threats, network vulnerabilities, and defensive strategies.",
      category: "Cybersecurity",
      organization: "Cisco",
      date: "2022",
      credentialId: "",
    },
    {
      title: "Cisco Cybersecurity Scholarship 2021/2022",
      imgUrl: "/ciscoScolarshipCertificate.png",
      description: "Participation certificate for completing the Cisco Cybersecurity Scholarship Italy 2021/2022 programme.",
      category: "Cybersecurity",
      organization: "Cisco",
      date: "2022",
      credentialId: "",
    },
    {
      title: "NDG Linux Essentials",
      imgUrl: "/ndgLinuxEssentialsCertificate.png",
      description:
        "Completion certificate for NDG Linux Essentials at Cisco Networking Academy, preparation for Linux Essentials Professional Development.",
      category: "Operating system",
      organization: "Cisco",
      date: "2022",
      credentialId: "",
    },
    {
      title: "HTML Level 1",
      imgUrl: "/htmlLv1CCACertificate.png",
      description: "Level 1 qualification in HTML issued by Cambridge Certification Authority.",
      category: "Web development",
      organization: "Cambridge Certification Authority",
      date: "2021",
      credentialId: "",
    },
    {
      title: "CSS Level 1",
      imgUrl: "/cssLv1CCACertificate.png",
      description: "Level 1 qualification in CSS issued by Cambridge Certification Authority.",
      category: "Web development",
      organization: "Cambridge Certification Authority",
      date: "2021",
      credentialId: "",
    },
    {
      title: "Javascript Level 1",
      imgUrl: "/javascriptLv1CCACertificate.png",
      description: "Level 1 qualification in JavaScript issued by Cambridge Certification Authority.",
      category: "Web development",
      organization: "Cambridge Certification Authority",
      date: "2021",
      credentialId: "",
    },
  ],
  seoByPath: {
    "/": {
      title: "Enrico Buza | Full stack web developer — sites and APIs",
      description:
        "Full stack web developer in Italy: React, TypeScript, Node.js, brochure sites, e-commerce, and bespoke backends for teams and SMBs. Portfolio, projects, and contact.",
    },
    "/about": {
      title: "About | Enrico Buza — web & backend developer",
      description:
        "About Enrico Buza: backend and frontend experience, how I work with clients and teams, TypeScript stack, and collaborations in Italy and remotely. CV-style profile for recruiters.",
    },
    "/projects": {
      title: "Web projects | Portfolio and case studies — Enrico Buza",
      description:
        "Selected real web projects: brochure sites, hospitality, integrations, React, Tailwind, and production deployment. Live previews via Microlink and client references.",
    },
    "/skills": {
      title: "Skills | React, TypeScript, Node.js, SQL — Enrico Buza",
      description:
        "Technical skills: React frontend, Node.js backend, SQL and graph databases, Git and Linux tooling. Practical focus on performance, security, and maintainability.",
    },
    "/certificates": {
      title: "Certificates | Cisco and web development training — Enrico Buza",
      description:
        "Cisco Networking Academy certifications, cybersecurity fundamentals, and HTML/CSS/JavaScript qualifications. A transparent view of my professional training path.",
    },
    "/contact": {
      title: "Contact | Website quote, collaborations — Enrico Buza",
      description:
        "Contact Enrico Buza for a website quote, freelance build, or job opportunity. Email and phone in Italy; replies within 1–2 business days. Remote collaborations welcome.",
    },
  },
  ui: {
    nav: [
      { id: "home", label: "Home", path: "/" },
      { id: "projects", label: "Projects", path: "/projects" },
      { id: "about", label: "About", path: "/about" },
      { id: "skills", label: "Skills", path: "/skills" },
      { id: "certificates", label: "Certificates", path: "/certificates" },
      { id: "contatti", label: "Contact", path: "/contact" },
    ],
    navAriaMain: "Main navigation",
    navOpenMenu: "Open menu",
    navCloseMenu: "Close menu",
    navMobileSection: "Navigation",
    hero: {
      kicker: "Web development · Full stack ·",
      titleGradient: "Sites and digital products that handle real traffic",
      lead: "TypeScript, React, and Node for a fast site, a converting e-commerce flow, or a tidy backend. I work with SMBs, hospitality, and teams—from the first call to go-live, without surprises.",
      ctaPrimary: "Let us talk about your project",
      ctaSecondary: "View projects",
      cardClientsTitle: "For clients",
      cardClientsBody:
        "Clear estimates, a modern stack, and code you can evolve. Ideal if you need a brochure site, a redesign, or integrations with payments and back-office tools.",
      cardRecruiterTitle: "For recruiters",
      cardRecruiterBody:
        "In-house experience on backends and microservices, React on the front, with attention to security and performance.",
      cardRecruiterLink: "Profile and background →",
      stackLine: "stack — React · TypeScript · Node · SQL · Neo4j",
    },
    about: {
      badge: "For recruiters, teams, and clients",
      mailSubject: "Contact from portfolio",
      ctaProject: "Project or application",
      portraitAlt: "Portrait",
      summaryHeading: "At a glance",
      storyHeading: "Why work with me",
      valuesSrOnly: "What I bring to projects",
      formCta: "Contact form",
    },
    contact: {
      badge: "Contact",
      linkAbout: "About",
      linkProjects: "Projects",
      whyHeading: "What you get when you reach out",
      directChannels: "Direct channels",
      emailLabel: "Email",
      phoneLabel: "Phone",
      locationLabel: "Location",
      socialHeading: "Social",
      formTitle: "Send a message",
      nameLabel: "Name",
      namePlaceholder: "Your name or company",
      emailPlaceholder: "you@example.com",
      messageLabel: "Message",
      messagePlaceholder: "E.g. project type, timeline, required stack…",
      submit: "Send message",
      submitting: "Sending…",
      validationRequired: "Please fill in all fields.",
      successMessage: "Message sent. I will get back to you soon.",
      errorPrefix: "Error:",
    },
    projects: {
      badge: "Case studies",
      title: "Web projects",
      intro:
        "Live sites and bespoke work. Previews update via Microlink (screenshots and metadata from the live page).",
      previewUnavailable: "Preview unavailable",
      previewLiveUnavailable: "Live preview unavailable",
      openSiteAria: "Open website in a new tab",
      visitSite: "Visit site",
      cardAlt: "Website preview:",
    },
    skills: {
      badge: "Stack & tooling",
      title: "Skills",
      intro: "Technologies I use in production every day, grouped by area—like a real codebase.",
      categories: [
        {
          title: "Frontend",
          skills: [
            { name: "React" },
            { name: "TypeScript" },
            { name: "Tailwind CSS" },
            { name: "HTML5" },
          ],
        },
        {
          title: "Backend",
          skills: [{ name: "Node.js" }, { name: "Express" }, { name: "MongoDB" }, { name: "MySQL" }],
        },
        {
          title: "Tooling",
          skills: [{ name: "Git" }, { name: "VS Code" }, { name: "Linux" }, { name: "Vercel" }],
        },
      ],
      closing:
        "I prefer TypeScript end-to-end, clear APIs, and repeatable deploys. Payments, technical SEO, or performance—we discuss it during discovery.",
      closingCode: 'import { focus } from "business"',
    },
    certificates: {
      badge: "Continuous learning",
      title: "Certificates",
      intro: "Networking, cybersecurity, and web development fundamentals.",
      filterAll: "All",
      statCerts: "Certificates",
      statOrgs: "Organisations",
      statAreas: "Areas",
    },
    footer: {
      tagline: "Full stack web development — Italy.",
      navAria: "Site links",
    },
    certificateCard: {
      alt: "Certificate",
      altFullscreen: "Full-screen certificate:",
      verify: "Verify certificate",
    },
  },
};
